import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-b border-white/15 bg-transparent py-4 text-base text-white outline-none transition-colors duration-300 placeholder:text-zinc-700 focus:border-[#E1A730]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", {
        description: "Thanks for reaching out — we'll be in touch shortly.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or email us directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 py-28 md:px-10 md:py-40">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 md:grid-cols-2 md:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <span className="font-mono text-[10px] tracking-[0.45em] text-zinc-500">CONTACT</span>
          <h2 className="mt-4 font-serif text-4xl leading-[1.15] tracking-tight sm:text-5xl">
            Let's make something worth watching.
          </h2>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-zinc-500">
            Tell us about your brand, your brief, or your wildest idea. We reply to every enquiry
            personally.
          </p>
          <div className="mt-8 flex flex-col gap-5">
            <a
              href="mailto:info@thisisbite.com"
              data-testid="contact-direct-email"
              className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-[#E1A730]"
            >
              INFO@THISISBITE.COM
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <a
              href="tel:+201010444516"
              data-testid="contact-phone-cairo"
              className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-[#E1A730]"
            >
              <Phone className="h-3.5 w-3.5 shrink-0" />
              <span>
                CAIRO OFFICE — <span className="whitespace-nowrap">+20 10 1044 4516</span>
              </span>
            </a>
            <a
              href="https://wa.me/966590306158"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-saudi"
              className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-[#E1A730]"
            >
              <MessageCircle className="h-3.5 w-3.5 shrink-0" />
              <span>
                SAUDI ARABIA — WHATSAPP{" "}
                <span className="whitespace-nowrap">+966 59 030 6158</span>
              </span>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="flex flex-col gap-8"
        >
          <div>
            <label
              htmlFor="contact-name"
              className="font-mono text-[10px] tracking-[0.35em] text-zinc-500"
            >
              NAME
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              data-testid="contact-name-input"
              className={inputClass}
            />
          </div>
          <div>
            <label
              htmlFor="contact-email"
              className="font-mono text-[10px] tracking-[0.35em] text-zinc-500"
            >
              EMAIL
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="you@company.com"
              data-testid="contact-email-input"
              className={inputClass}
            />
          </div>
          <div>
            <label
              htmlFor="contact-message"
              className="font-mono text-[10px] tracking-[0.35em] text-zinc-500"
            >
              MESSAGE
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={4}
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about your project"
              data-testid="contact-message-input"
              className={`${inputClass} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="group mt-2 inline-flex w-fit items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.3em] transition-colors duration-300 hover:border-[#E1A730] hover:bg-[#E1A730] hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? "SENDING…" : "SEND MESSAGE"}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
